import { c as _c } from "react/compiler-runtime";
import type { ApiNodeDataCodeExec, CodeExecutionLanguage } from '@onefootprint/request-types/dashboard';
import { Divider, Form, InlineAlert, PythonCodeEditor } from '@onefootprint/ui';
import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import { useGetNodeConfigContext } from '@/components/playbook-builder/hooks/use-get-node-config-context';
import type { NodeProps } from '@/playbook-builder/playbook-builder.types';
import { type OutputType, outputSchemaFromType, outputTypeFromSchema, outputTypes } from '@/playbook-builder/utils/output-type';
import type { ApplyEditMutation } from '../../base-node';
type CodeExecNodeFormProps = {
  node: NodeProps<ApiNodeDataCodeExec>;
  mutation: ApplyEditMutation;
  canEdit: boolean;
};
type CodeExecFormData = {
  name: string;
  language: CodeExecutionLanguage;
  program: string;
  dataKey: string;
  outputType: OutputType;
};
const languageOptions: {
  value: CodeExecutionLanguage;
  label: string;
}[] = [{
  value: 'python',
  label: 'Python'
}];
export const CodeExecNodeForm = t0 => {
  const $0 = _c(108);
  const {
    node,
    mutation,
    canEdit
  } = t0;
  const {
    toggle
  } = useDirty();
  const t1 = !canEdit;
  const t2 = node.data.name;
  const t3 = node.data.language;
  const t4 = node.data.program;
  const t5 = node.data.dataKey;
  let t6;
  if ($0[0] !== node.data.outputSchema) {
    t6 = outputTypeFromSchema(node.data.outputSchema);
    $0[0] = node.data.outputSchema;
    $0[1] = t6;
  } else {
    t6 = $0[1];
  }
  let t7;
  if ($0[2] !== node.data.dataKey || $0[3] !== node.data.language || $0[4] !== node.data.name || $0[5] !== node.data.program || $0[6] !== t6) {
    t7 = {
      name: t2,
      language: t3,
      program: t4,
      dataKey: t5,
      outputType: t6
    };
    $0[2] = node.data.dataKey;
    $0[3] = node.data.language;
    $0[4] = node.data.name;
    $0[5] = node.data.program;
    $0[6] = t6;
    $0[7] = t7;
  } else {
    t7 = $0[7];
  }
  let t8;
  if ($0[8] !== t1 || $0[9] !== t7) {
    t8 = {
      disabled: t1,
      defaultValues: t7
    };
    $0[8] = t1;
    $0[9] = t7;
    $0[10] = t8;
  } else {
    t8 = $0[10];
  }
  const form = useForm(t8);
  const config = useGetNodeConfigContext(node.id, "templatable");
  let t9;
  if ($0[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      keyPrefix: "codeExecNode"
    };
    $0[11] = t9;
  } else {
    t9 = $0[11];
  }
  const {
    t
  } = useTranslation("playbook-builder", t9);
  let t10;
  if ($0[12] !== t) {
    t10 = outputTypes.map(value => ({
      value,
      label: t($ => $.outputType.options[value])
    }));
    $0[12] = t;
    $0[13] = t10;
  } else {
    t10 = $0[13];
  }
  const outputTypeOptions = t10;
  let t11;
  if ($0[14] !== form.formState.isDirty || $0[15] !== toggle) {
    t11 = () => {
      toggle("code-exec-form", form.formState.isDirty);
    };
    $0[14] = form.formState.isDirty;
    $0[15] = toggle;
    $0[16] = t11;
  } else {
    t11 = $0[16];
  }
  let t12;
  if ($0[17] !== form.formState.isDirty) {
    t12 = [form.formState.isDirty];
    $0[17] = form.formState.isDirty;
    $0[18] = t12;
  } else {
    t12 = $0[18];
  }
  useEffect(t11, t12);
  let t13;
  if ($0[19] !== mutation || $0[20] !== node.id) {
    t13 = async data => {
      ;
      try {
        await mutation.mutateAsync({
          kind: "update_node",
          nodeId: node.id,
          nodeData: {
            kind: "code_exec",
            name: data.name,
            language: data.language,
            program: data.program,
            dataKey: data.dataKey,
            outputSchema: outputSchemaFromType(data.outputType)
          }
        });
      } catch (t14) {
        const error = t14;
        console.error("Error updating playbook session:", error);
      }
    };
    $0[19] = mutation;
    $0[20] = node.id;
    $0[21] = t13;
  } else {
    t13 = $0[21];
  }
  const handleFormSubmit = t13;
  let t14;
  if ($0[22] !== form || $0[23] !== handleFormSubmit) {
    t14 = form.handleSubmit(handleFormSubmit);
    $0[22] = form;
    $0[23] = handleFormSubmit;
    $0[24] = t14;
  } else {
    t14 = $0[24];
  }
  let t15;
  if ($0[25] !== t) {
    t15 = t(_temp);
    $0[25] = t;
    $0[26] = t15;
  } else {
    t15 = $0[26];
  }
  let t16;
  if ($0[27] !== t) {
    t16 = t(_temp2);
    $0[27] = t;
    $0[28] = t16;
  } else {
    t16 = $0[28];
  }
  let t17;
  if ($0[29] !== t) {
    t17 = t(_temp3);
    $0[29] = t;
    $0[30] = t17;
  } else {
    t17 = $0[30];
  }
  let t18;
  if ($0[31] !== t17) {
    t18 = {
      required: t17
    };
    $0[31] = t17;
    $0[32] = t18;
  } else {
    t18 = $0[32];
  }
  let t19;
  if ($0[33] !== t15 || $0[34] !== t16 || $0[35] !== t18) {
    t19 = <div className="bg-secondary p-4"><Form.Input label={t15} name="name" placeholder={t16} size="compact" rules={t18} /></div>;
    $0[33] = t15;
    $0[34] = t16;
    $0[35] = t18;
    $0[36] = t19;
  } else {
    t19 = $0[36];
  }
  let t20;
  if ($0[37] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Divider />;
    $0[37] = t20;
  } else {
    t20 = $0[37];
  }
  let t21;
  if ($0[38] !== t19) {
    t21 = <section>{t19}{t20}</section>;
    $0[38] = t19;
    $0[39] = t21;
  } else {
    t21 = $0[39];
  }
  let t22;
  if ($0[40] !== t) {
    t22 = t(_temp4);
    $0[40] = t;
    $0[41] = t22;
  } else {
    t22 = $0[41];
  }
  let t23;
  if ($0[42] !== t22) {
    t23 = <Form.Select label={t22} name="language" options={languageOptions} size="compact" />;
    $0[42] = t22;
    $0[43] = t23;
  } else {
    t23 = $0[43];
  }
  let t24;
  if ($0[44] !== t) {
    t24 = t(_temp5);
    $0[44] = t;
    $0[45] = t24;
  } else {
    t24 = $0[45];
  }
  let t25;
  if ($0[46] !== t24) {
    t25 = <label htmlFor="program" className="text-body-2">{t24}</label>;
    $0[46] = t24;
    $0[47] = t25;
  } else {
    t25 = $0[47];
  }
  let t26;
  if ($0[48] !== t) {
    t26 = t(_temp6);
    $0[48] = t;
    $0[49] = t26;
  } else {
    t26 = $0[49];
  }
  let t27;
  if ($0[50] !== t26) {
    t27 = <p className="text-caption-2 text-tertiary">{t26}</p>;
    $0[50] = t26;
    $0[51] = t27;
  } else {
    t27 = $0[51];
  }
  const t28 = form.control;
  let t29;
  if ($0[52] !== t) {
    t29 = t(_temp7);
    $0[52] = t;
    $0[53] = t29;
  } else {
    t29 = $0[53];
  }
  let t30;
  if ($0[54] !== t29) {
    t30 = {
      required: t29
    };
    $0[54] = t29;
    $0[55] = t30;
  } else {
    t30 = $0[55];
  }
  let t31;
  if ($0[56] !== canEdit || $0[57] !== config?.autocomplete || $0[58] !== t) {
    t31 = t32 => {
      const {
        field,
        fieldState
      } = t32;
      return <div className="flex flex-col gap-2 w-full relative"><PythonCodeEditor value={field.value} onChange={field.onChange} autocompleteItems={config?.autocomplete} disabled={!canEdit || field.disabled} placeholder={t(_temp8)} />{!!fieldState.error && <InlineAlert variant="error">{fieldState.error.message}</InlineAlert>}</div>;
    };
    $0[56] = canEdit;
    $0[57] = config?.autocomplete;
    $0[58] = t;
    $0[59] = t31;
  } else {
    t31 = $0[59];
  }
  let t32;
  if ($0[60] !== form.control || $0[61] !== t30 || $0[62] !== t31) {
    t32 = <Controller name="program" control={t28} rules={t30} render={t31} />;
    $0[60] = form.control;
    $0[61] = t30;
    $0[62] = t31;
    $0[63] = t32;
  } else {
    t32 = $0[63];
  }
  let t33;
  if ($0[64] !== t25 || $0[65] !== t27 || $0[66] !== t32) {
    t33 = <div className="flex flex-col gap-2">{t25}{t27}{t32}</div>;
    $0[64] = t25;
    $0[65] = t27;
    $0[66] = t32;
    $0[67] = t33;
  } else {
    t33 = $0[67];
  }
  let t34;
  if ($0[68] !== t23 || $0[69] !== t33) {
    t34 = <div className="flex flex-col gap-4 ">{t23}{t33}</div>;
    $0[68] = t23;
    $0[69] = t33;
    $0[70] = t34;
  } else {
    t34 = $0[70];
  }
  let t35;
  if ($0[71] !== t) {
    t35 = t(_temp9);
    $0[71] = t;
    $0[72] = t35;
  } else {
    t35 = $0[72];
  }
  let t36;
  if ($0[73] !== t) {
    t36 = t(_temp10);
    $0[73] = t;
    $0[74] = t36;
  } else {
    t36 = $0[74];
  }
  let t37;
  if ($0[75] !== t36) {
    t37 = {
      required: t36
    };
    $0[75] = t36;
    $0[76] = t37;
  } else {
    t37 = $0[76];
  }
  let t38;
  if ($0[77] !== t) {
    t38 = t(_temp11);
    $0[77] = t;
    $0[78] = t38;
  } else {
    t38 = $0[78];
  }
  let t39;
  if ($0[79] !== t35 || $0[80] !== t37 || $0[81] !== t38) {
    t39 = <div className="flex-1"><Form.Input name="dataKey" placeholder={t35} size="compact" rules={t37} label={t38} addon="data." /></div>;
    $0[79] = t35;
    $0[80] = t37;
    $0[81] = t38;
    $0[82] = t39;
  } else {
    t39 = $0[82];
  }
  let t40;
  if ($0[83] !== t) {
    t40 = t(_temp12);
    $0[83] = t;
    $0[84] = t40;
  } else {
    t40 = $0[84];
  }
  let t41;
  if ($0[85] !== outputTypeOptions || $0[86] !== t40) {
    t41 = <div className="flex-1"><Form.Select label={t40} name="outputType" options={outputTypeOptions} size="compact" /></div>;
    $0[85] = outputTypeOptions;
    $0[86] = t40;
    $0[87] = t41;
  } else {
    t41 = $0[87];
  }
  let t42;
  if ($0[88] !== t39 || $0[89] !== t41) {
    t42 = <div className="flex gap-2 items-start">{t39}{t41}</div>;
    $0[88] = t39;
    $0[89] = t41;
    $0[90] = t42;
  } else {
    t42 = $0[90];
  }
  let t43;
  if ($0[91] !== t) {
    t43 = t(_temp13);
    $0[91] = t;
    $0[92] = t43;
  } else {
    t43 = $0[92];
  }
  let t44;
  if ($0[93] !== t43) {
    t44 = <p className="text-caption-2 text-tertiary">{t43}</p>;
    $0[93] = t43;
    $0[94] = t44;
  } else {
    t44 = $0[94];
  }
  let t45;
  if ($0[95] !== t42 || $0[96] !== t44) {
    t45 = <div className="flex flex-col gap-2">{t42}{t44}</div>;
    $0[95] = t42;
    $0[96] = t44;
    $0[97] = t45;
  } else {
    t45 = $0[97];
  }
  let t46;
  if ($0[98] !== t34 || $0[99] !== t45) {
    t46 = <div className="flex flex-col gap-4 p-4">{t34}{t45}</div>;
    $0[98] = t34;
    $0[99] = t45;
    $0[100] = t46;
  } else {
    t46 = $0[100];
  }
  let t47;
  if ($0[101] !== t14 || $0[102] !== t21 || $0[103] !== t46) {
    t47 = <form id="code-exec-form" onSubmit={t14} className="flex flex-col border border-tertiary rounded">{t21}{t46}</form>;
    $0[101] = t14;
    $0[102] = t21;
    $0[103] = t46;
    $0[104] = t47;
  } else {
    t47 = $0[104];
  }
  let t48;
  if ($0[105] !== form || $0[106] !== t47) {
    t48 = <FormProvider {...form}>{t47}</FormProvider>;
    $0[105] = form;
    $0[106] = t47;
    $0[107] = t48;
  } else {
    t48 = $0[107];
  }
  return t48;
};
function _temp($_0) {
  return $_0.nameLabel;
}
function _temp2($_1) {
  return $_1.namePlaceholder;
}
function _temp3($_2) {
  return $_2.nameRequired;
}
function _temp4($_3) {
  return $_3.languageLabel;
}
function _temp5($_4) {
  return $_4.programLabel;
}
function _temp6($_5) {
  return $_5.programDescription;
}
function _temp7($_6) {
  return $_6.programRequired;
}
function _temp8($_7) {
  return $_7.programPlaceholder;
}
function _temp9($_8) {
  return $_8.dataKeyPlaceholder;
}
function _temp10($_9) {
  return $_9.dataKeyRequired;
}
function _temp11($_10) {
  return $_10.dataKeyLabel;
}
function _temp12($_11) {
  return $_11.outputType.label;
}
function _temp13($_12) {
  return $_12.outputType.hint;
}